import React, { useEffect, useState } from "react";
import api from "../../services/api";
import { PageContainer, PageHeader, Card, Table, Thead, Th, Td, TRow, Badge, Muted, EmptyState } from "../ui";

interface DrawerCount {
  id: string;
  count_type: string;
  denominations: Record<string, number>;
  total: number;
  counted_by_name: string;
  counted_at: string;
  session_id: string | null;
}

const DENOM_ORDER = [
  "Pennies", "Nickels", "Dimes", "Quarters", "Half Dollars", "Dollar Coins",
  "$1 Bills", "$5 Bills", "$10 Bills", "$20 Bills", "$50 Bills", "$100 Bills",
];

const DENOM_VALUES: Record<string, number> = {
  "Pennies": 0.01, "Nickels": 0.05, "Dimes": 0.10, "Quarters": 0.25,
  "Half Dollars": 0.50, "Dollar Coins": 1.00, "$1 Bills": 1.00,
  "$5 Bills": 5.00, "$10 Bills": 10.00, "$20 Bills": 20.00,
  "$50 Bills": 50.00, "$100 Bills": 100.00,
};

export default function DrawerHistory() {
  const [counts, setCounts] = useState<DrawerCount[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    api.get("/checklists/drawer-counts?limit=50").then((r) => setCounts(r.data)).catch(() => {});
  }, []);

  const toggleExpand = (id: string) => {
    setExpanded(expanded === id ? null : id);
  };

  return (
    <PageContainer>
      <PageHeader
        title="Drawer Count History"
        subtitle="Every opening and closing drawer count, saved automatically"
      />

      {counts.length === 0 ? (
        <EmptyState
          icon="&#128176;"
          message="No drawer counts yet"
          detail="Counts are saved automatically when opening or closing the drawer from the register"
        />
      ) : (
        <div className="space-y-3">
          {counts.map((count) => {
            const isExpanded = expanded === count.id;
            const denoms = count.denominations || {};
            const date = new Date(count.counted_at);

            return (
              <Card key={count.id} padding="p-0" className="overflow-hidden">
                {/* Summary row - clickable */}
                <button
                  onClick={() => toggleExpand(count.id)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left transition-all hover:bg-gray-50"
                >
                  <div className="flex items-center gap-4">
                    <Badge variant={count.count_type === "opening" ? "green" : "blue"}>
                      {count.count_type}
                    </Badge>
                    <div>
                      <div className="font-semibold" style={{ color: "var(--rbw-navy)" }}>
                        {date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
                      </div>
                      <div className="text-xs"><Muted>{date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</Muted></div>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <div className="text-xs"><Muted>Counted by</Muted></div>
                      <div className="text-sm font-medium" style={{ color: "var(--rbw-navy)" }}>{count.counted_by_name}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs"><Muted>Total</Muted></div>
                      <div className="text-xl font-bold" style={{ color: "var(--rbw-navy)" }}>${Number(count.total).toFixed(2)}</div>
                    </div>
                    <div className="text-sm" style={{ color: "var(--rbw-gray-dark)", transform: isExpanded ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>
                      &#9660;
                    </div>
                  </div>
                </button>

                {/* Expanded denomination breakdown */}
                {isExpanded && (
                  <div className="px-5 pb-4" style={{ borderTop: "1px solid var(--rbw-border)" }}>
                    <div className="grid grid-cols-4 gap-2 pt-4">
                      {DENOM_ORDER.map((denomName) => {
                        const qty = denoms[denomName] || 0;
                        if (qty === 0) return null;
                        const subtotal = qty * (DENOM_VALUES[denomName] || 0);
                        return (
                          <div
                            key={denomName}
                            className="flex items-center justify-between px-3 py-2 rounded-lg"
                            style={{ background: "var(--rbw-bg)" }}
                          >
                            <div>
                              <span className="text-xs font-medium" style={{ color: "var(--rbw-navy)" }}>{denomName}</span>
                              <span className="text-xs ml-1.5" style={{ color: "var(--rbw-gray-dark)" }}>x{qty}</span>
                            </div>
                            <span className="text-xs font-bold" style={{ color: "var(--rbw-gold-dark)" }}>
                              ${subtotal.toFixed(2)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </PageContainer>
  );
}
